import { idx } from "./types";
/**
 * Returns the set of numbers already present in the same row, column, and box
 * as the given cell. Used by the generator and solver to prune candidates.
 */
export function getPeers(grid, row, col) {
    const peers = new Set();
    // Row
    for (let c = 0; c < 9; c++) {
        const v = grid[idx(row, c)] ?? 0;
        if (v !== 0)
            peers.add(v);
    }
    // Column
    for (let r = 0; r < 9; r++) {
        const v = grid[idx(r, col)] ?? 0;
        if (v !== 0)
            peers.add(v);
    }
    // 3×3 box
    const boxRow = Math.floor(row / 3) * 3;
    const boxCol = Math.floor(col / 3) * 3;
    for (let r = boxRow; r < boxRow + 3; r++) {
        for (let c = boxCol; c < boxCol + 3; c++) {
            const v = grid[idx(r, c)] ?? 0;
            if (v !== 0)
                peers.add(v);
        }
    }
    return peers;
}
/**
 * Returns true if placing `value` at (row, col) is legal in the current grid.
 */
export function isValidPlacement(grid, row, col, value) {
    if (value < 1 || value > 9)
        return false;
    return !getPeers(grid, row, col).has(value);
}
/**
 * Returns true if the complete grid is a valid, fully-solved Sudoku.
 */
export function isSolved(grid) {
    if (grid.length !== 81)
        return false;
    const expected = new Set([1, 2, 3, 4, 5, 6, 7, 8, 9]);
    const setEq = (a, b) => a.size === b.size && [...a].every((v) => b.has(v));
    // Check all rows
    for (let r = 0; r < 9; r++) {
        const vals = new Set(grid.slice(r * 9, r * 9 + 9));
        if (!setEq(vals, expected))
            return false;
    }
    // Check all columns
    for (let c = 0; c < 9; c++) {
        const vals = new Set();
        for (let r = 0; r < 9; r++)
            vals.add(grid[idx(r, c)] ?? 0);
        if (!setEq(vals, expected))
            return false;
    }
    // Check all 3×3 boxes
    for (let box = 0; box < 9; box++) {
        const vals = new Set();
        const boxRow = Math.floor(box / 3) * 3;
        const boxCol = (box % 3) * 3;
        for (let r = boxRow; r < boxRow + 3; r++) {
            for (let c = boxCol; c < boxCol + 3; c++) {
                vals.add(grid[idx(r, c)] ?? 0);
            }
        }
        if (!setEq(vals, expected))
            return false;
    }
    return true;
}
/**
 * Validates a player's partial board against the known solution.
 * Returns an array of cell indexes where the player's value conflicts with
 * the solution (non-zero values only).
 */
export function getConflicts(playerGrid, solution) {
    const conflicts = [];
    for (let i = 0; i < 81; i++) {
        const pv = playerGrid[i] ?? 0;
        const sv = solution[i] ?? 0;
        if (pv !== 0 && pv !== sv) {
            conflicts.push(i);
        }
    }
    return conflicts;
}
/**
 * Returns all cell indexes that share a row, col, or box with the given index.
 * Used for highlighting peers in the UI.
 */
export function getPeerIndexes(cellIndex) {
    const row = Math.floor(cellIndex / 9);
    const col = cellIndex % 9;
    const peers = new Set();
    for (let c = 0; c < 9; c++)
        peers.add(idx(row, c));
    for (let r = 0; r < 9; r++)
        peers.add(idx(r, col));
    const boxRow = Math.floor(row / 3) * 3;
    const boxCol = Math.floor(col / 3) * 3;
    for (let r = boxRow; r < boxRow + 3; r++) {
        for (let c = boxCol; c < boxCol + 3; c++) {
            peers.add(idx(r, c));
        }
    }
    peers.delete(cellIndex); // Don't include the cell itself
    return peers;
}
